import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';


const GlossarySidebar = ({ 
  glossaryWords, 
  isOpen, 
  onClose,
  selectedWord 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);

  const filteredWords = glossaryWords?.filter(word =>
    word?.word?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    word?.definition?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handlePlayPronunciation = (wordId) => {
    if (playingAudio === wordId) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(wordId);
      setTimeout(() => setPlayingAudio(null), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-foreground/50 z-[1500] lg:hidden"
        onClick={onClose}
      />
      <div className={`fixed top-0 right-0 bottom-0 w-96 max-w-full bg-background border-l border-border z-[1600] flex flex-col shadow-elevation-3 ${
        isOpen ? 'animate-slide-in-right' : ''
      }`}>
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="BookOpen" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Glossary</h2>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md hover:bg-muted transition-colors duration-150"
            aria-label="Close glossary"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 border-b border-border">
          <Input
            type="search"
            placeholder="Search words..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filteredWords?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No words found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredWords?.map((word) => (
                <div
                  key={word?.id}
                  className={`p-4 rounded-lg border transition-all duration-150 ${
                    selectedWord === word?.word
                      ? 'bg-accent/10 border-accent' :'bg-card border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-base font-semibold text-foreground capitalize">
                      {word?.word}
                    </h3>
                    {word?.hasAudio && (
                      <button
                        onClick={() => handlePlayPronunciation(word?.id)}
                        className="flex-shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors duration-150"
                        aria-label={`Play pronunciation for ${word?.word}`}
                      >
                        <Icon 
                          name={playingAudio === word?.id ? 'Volume2' : 'Volume'} 
                          size={16} 
                          className="text-secondary" 
                        />
                      </button>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {word?.definition}
                  </p>

                  {word?.example && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-1">Example:</p>
                      <p className="text-sm text-foreground italic">"{word?.example}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border">
          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
            <Icon name="Lightbulb" size={16} className="text-accent flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Click on underlined words in the script to see their definitions here
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlossarySidebar;